import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Timer, Briefcase, PhoneCall, Check } from "lucide-react";
import { fadeInUpSmall } from "../../lib/animations";
import Mascot from "../Mascot";

/* ─────────────────────────────────────────────────────────────
   ProofSection — 3 verified proof blocks.
   COMPLIANCE RULE: every statistic here is a CITED industry
   benchmark or a statement about what the product does.
   No number may be presented as AdWup's own results.
   ───────────────────────────────────────────────────────────── */

const leadStats = [
  {
    id: "stat-cpl",
    value: "~$17",
    desc: "Average cost per real estate lead on Meta — one of the lowest of any industry",
    source: "WordStream / LocaliQ 2025",
  },
  {
    id: "stat-ctr",
    value: "~3.7%",
    desc: "Real estate lead-ad click-through rate — one of the highest of any industry",
    source: "WordStream / LocaliQ 2025",
  },
  {
    id: "stat-retargeting",
    value: "50–70%",
    desc: "Lower cost per lead using warm/retargeting audiences vs. cold prospecting",
    source: "Focus Digital 2025",
  },
];

const speedStats = [
  {
    id: "stat-first-response",
    value: "78%",
    desc: "of buyers go with the FIRST company to respond",
    source: "MIT / InsideSales",
  },
  {
    id: "stat-one-minute",
    value: "+391%",
    desc: "more conversions when you respond within 1 minute vs. 2 minutes",
    source: "Velocify, 3.5M leads",
  },
  {
    id: "stat-avg-response",
    value: "47 hrs",
    desc: "average business response time — 23% never respond at all",
    source: "Harvard Business Review",
  },
];

const agencyPoints = [
  "No Ads Manager. No hiring. No waiting.",
  "Agencies charge 10–20% of your ad spend. AdWup charges just 3%.",
  "You focus on the only thing that closes deals: your clients.",
];

export function ProofSection() {
  return (
    <>
      {/* ── BLOCK 1: Get more leads ── */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-white" data-testid="proof-leads-block">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={fadeInUpSmall.initial}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 px-4 py-2 mb-6 rounded-full">
              <TrendingUp className="w-4 h-4 text-[#15803d]" />
              <span className="text-[#15803d] font-semibold text-xs tracking-widest uppercase">Get More Leads</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Real estate ads perform on Meta. <span className="text-[#15803d]">AdWup squeezes every dollar.</span>
            </h2>
            <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
              Industry data, not promises. Your results depend on your market, listing, and creative.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {leadStats.map((stat, i) => (
              <motion.div
                key={stat.id}
                initial={fadeInUpSmall.initial}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ delay: i * 0.08 }}
                className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow"
                data-testid={stat.id}
              >
                <p className="text-4xl md:text-5xl font-extrabold text-[#39FF14] mb-3 [text-shadow:0_1px_1px_rgba(0,0,0,0.15)]">{stat.value}</p>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">{stat.desc}</p>
                <p className="text-[11px] text-gray-400">Source: {stat.source}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 2: Close them before anyone else ── */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-[#0A0A0F] overflow-hidden" data-testid="proof-speed-block">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-5xl mx-auto relative">
          <motion.div
            initial={fadeInUpSmall.initial}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#8A2BE2]/15 border border-[#8A2BE2]/30 px-4 py-2 mb-6 rounded-full">
              <Timer className="w-4 h-4 text-[#c084fc]" />
              <span className="text-[#c084fc] font-semibold text-xs tracking-widest uppercase">Close Them Before Anyone Else</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 max-w-3xl mx-auto">
              Agents don't lose deals to bad leads. They lose them by <span className="text-[#39FF14]">responding too late.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {speedStats.map((stat, i) => (
              <motion.div
                key={stat.id}
                initial={fadeInUpSmall.initial}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ delay: i * 0.08 }}
                className="bg-white/[0.04] rounded-2xl border border-white/10 p-6 text-center"
                data-testid={stat.id}
              >
                <p className="text-4xl md:text-5xl font-extrabold text-[#39FF14] mb-3">{stat.value}</p>
                <p className="text-sm text-gray-300 leading-relaxed mb-3">{stat.desc}</p>
                <p className="text-[11px] text-gray-500">Source: {stat.source}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={fadeInUpSmall.initial}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            className="max-w-2xl mx-auto bg-gradient-to-r from-[#39FF14]/15 to-[#8A2BE2]/15 border border-[#39FF14]/30 rounded-2xl px-6 py-5 flex items-center justify-center gap-3 text-center"
            data-testid="proof-speed-closing"
          >
            <PhoneCall className="w-5 h-5 text-[#39FF14] flex-shrink-0" />
            <p className="text-white font-semibold text-base md:text-lg">
              AdWup calls every new lead in under 60 seconds. <span className="text-[#39FF14]">You're first. Every time.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── BLOCK 3: Fire the agency ── */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-gray-50" data-testid="proof-agency-block">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={fadeInUpSmall.initial}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 md:p-12"
          >
            <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#8A2BE2]/10 px-4 py-2 mb-6 rounded-full">
                  <Briefcase className="w-4 h-4 text-[#8A2BE2]" />
                  <span className="text-[#8A2BE2] font-semibold text-xs tracking-widest uppercase">Fire The Agency</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Your whole marketing team. <span className="text-[#8A2BE2]">For less than one freelancer's invoice.</span>
                </h2>
                <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-6">
                  Hiring someone to run your ads costs $1,000–$3,000/month, plus setup fees, plus a separate copywriter and designer. AdWup does all of it from $149/mo — paste a listing link and the AI workflow builds the full campaign in minutes.
                </p>
                <ul className="space-y-3">
                  {agencyPoints.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-sm md:text-base text-gray-700">
                      <Check className="w-5 h-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="hidden md:flex flex-col items-center justify-center">
                <Mascot variant="purple-kawaii" size={140} animation="float" alt="AdWup mascot — your AI marketing team" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
